import { useState } from 'react';
import { Check, Lock, UserPlus, Plus, Handshake, Building2 } from 'lucide-react';
import { useAuth } from '@shared/context/useAuth';
import { taskRequestApi } from '../services/taskRequestApi';
import type { ChecklistStep, TaskStatus, TaskType } from '../types/taskRequest';

interface TaskChecklistProps {
  taskId: number;
  checklist: ChecklistStep[];
  taskStatus: TaskStatus;
  taskType: TaskType;
  assignedToUserName?: string;
  onUpdated: () => void;
  onAssignClick: () => void;
  onSourceAction: () => void;
}

export default function TaskChecklist({
  taskId,
  checklist,
  taskStatus,
  taskType,
  assignedToUserName,
  onUpdated,
  onAssignClick,
  onSourceAction,
}: Readonly<TaskChecklistProps>) {
  const { user } = useAuth();
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);

  const isReadOnly = taskStatus === 'Completed' || taskStatus === 'Rejected';
  const completedCount = checklist.filter((s) => s.completed).length;

  const canToggle = (index: number, step: ChecklistStep): boolean => {
    if (isReadOnly || updatingKey !== null) return false;
    // "assigned" step is auto-managed — don't allow manual toggle
    if (step.key === 'assigned') return false;
    // Can always uncheck a completed step
    if (step.completed) return true;
    // Can only check if all prior steps are completed
    for (let i = 0; i < index; i++) {
      if (!checklist[i].completed) return false;
    }
    return true;
  };

  const isNextStep = (index: number, step: ChecklistStep): boolean => {
    if (step.completed) return false;
    for (let i = 0; i < index; i++) {
      if (!checklist[i].completed) return false;
    }
    return true;
  };

  const handleToggle = async (step: ChecklistStep) => {
    if (updatingKey !== null) return;
    setUpdatingKey(step.key);
    try {
      await taskRequestApi.updateChecklistStep(taskId, {
        stepKey: step.key,
        completed: !step.completed,
        completedByUserId: user?.userId,
        completedByUserName: user?.displayName,
      });
      onUpdated();
    } catch (err) {
      console.error('Failed to update checklist step:', err);
    } finally {
      setUpdatingKey(null);
    }
  };

  const formatStepDate = (dateStr?: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const renderStepAction = (step: ChecklistStep) => {
    if (isReadOnly) return null;

    if (step.key === 'assigned') {
      return (
        <button
          type="button"
          className="task-checklist-action-btn"
          onClick={(e) => { e.stopPropagation(); onAssignClick(); }}
          title={assignedToUserName ? `Reassign (currently ${assignedToUserName})` : 'Assign'}
        >
          <UserPlus size={14} />
          {assignedToUserName ? 'Reassign' : 'Assign'}
        </button>
      );
    }

    if (step.key === 'initial_contact') {
      // ImAddClinic: the clinic already exists (created by the IM simple-add), so the
      // action opens it to complete + verify — not create a new one.
      let label: string;
      let Icon: typeof Plus;
      if (taskType === 'ImAddClinic') {
        label = 'Open Clinic';
        Icon = Building2;
      } else if (taskType === 'SetupNewClinic') {
        label = 'Add Clinic';
        Icon = Plus;
      } else {
        label = 'Create Relationship';
        Icon = Handshake;
      }
      return (
        <button
          type="button"
          className="task-checklist-action-btn"
          onClick={(e) => { e.stopPropagation(); onSourceAction(); }}
        >
          <Icon size={14} />
          {label}
        </button>
      );
    }

    return null;
  };

  return (
    <div className="task-checklist-section">
      <div className="task-checklist-header">
        <h3>Checklist</h3>
        <span className="task-checklist-progress">
          {completedCount} of {checklist.length} complete
        </span>
      </div>

      {/* Progress bar */}
      <div className="task-checklist-progress-bar">
        <div
          className="task-checklist-progress-fill"
          style={{ width: `${checklist.length > 0 ? (completedCount / checklist.length) * 100 : 0}%` }}
        />
      </div>

      <div className="task-checklist-items">
        {checklist.map((step, index) => {
          const toggleable = canToggle(index, step);
          const next = isNextStep(index, step);
          const locked = !step.completed && !next && !isReadOnly;

          let itemClass = 'task-checklist-item';
          if (step.completed) itemClass += ' task-checklist-item-completed';
          else if (next) itemClass += ' task-checklist-item-next';
          else if (locked) itemClass += ' task-checklist-item-locked';

          return (
            <div key={step.key} className={itemClass}>
              <div className="task-checklist-check-area">
                <input
                  type="checkbox"
                  className="task-checklist-checkbox"
                  checked={step.completed}
                  disabled={!toggleable}
                  onChange={() => handleToggle(step)}
                />
              </div>
              <div className="task-checklist-content">
                <div className="task-checklist-label-row">
                  <span className={`task-checklist-label ${step.completed ? 'task-checklist-label-completed' : ''} ${locked ? 'task-checklist-label-locked' : ''}`}>
                    {step.label}
                  </span>
                  {locked && <Lock size={12} className="task-checklist-lock-icon" />}
                  {step.completed && <Check size={14} className="task-checklist-check-icon" />}
                  {renderStepAction(step)}
                </div>
                {step.completed && step.completedByUserName && (
                  <span className="task-checklist-meta">
                    {step.completedByUserName}{step.completedAt ? `, ${formatStepDate(step.completedAt)}` : ''}
                  </span>
                )}
              </div>
              {updatingKey === step.key && (
                <div className="task-checklist-spinner" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
